// Settings → People & Access → Picker Visibility: configure WHICH user roles
// appear in each people picker (Requested by / Assigned to / Shared with) per
// work type. The matrix is stored as one settings blob keyed "<work_type>:<field>"
// → allowed role codes. An empty cell means "all roles" (backward compatible).
//
// Notes on which cells apply (mirrors the backend roster wiring):
//  - "Requested by" for tickets & work orders is a free-text / contact picker
//    (listClients), not a role-bearing user picker, so those cells are disabled.
//  - "Shared with" only exists for Action Items (the 0090 share picker), so the
//    other work types' shared cells are disabled.

import { useEffect, useMemo, useState } from "react";
import { getRoleVisibility, listRoles, setRoleVisibility } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import type { RoleRow, WorkTypeId, PeopleFieldId } from "../lib/types";
import { Alert, Loading, ErrorState, Spinner } from "./States";

const WORK_TYPES: { id: WorkTypeId; label: string }[] = [
  { id: "tickets", label: "Tickets" },
  { id: "projects", label: "Projects" },
  { id: "action_items", label: "Action Items" },
  { id: "work_orders", label: "Work Orders" },
  { id: "change_management", label: "Change Management" },
];

const FIELDS: { id: PeopleFieldId; label: string }[] = [
  { id: "requester", label: "Requested by" },
  { id: "assignee", label: "Assigned to" },
  { id: "shared", label: "Shared with" },
  { id: "contributor", label: "Contributors" },
];

/** Which (work_type, field) cells map to an actual role-bearing user picker. A
 *  cell that isn't applicable is shown disabled with an inline note. */
function cellApplies(wt: WorkTypeId, field: PeopleFieldId): boolean {
  if (field === "shared") return wt === "action_items"; // only Action Items have a share picker
  if (field === "contributor") return wt === "change_management"; // only Change Management has contributors
  if (field === "requester") return wt === "change_management"; // others use free-text/contact requesters
  return true; // assignee applies everywhere
}

const cellKey = (wt: WorkTypeId, field: PeopleFieldId) => `${wt}:${field}`;

export function RoleVisibilityTab() {
  const rolesApi = useApi((s) => listRoles(s), []);
  const matrixApi = useApi((s) => getRoleVisibility(s), []);
  const saveMut = useMutation((m: Record<string, string[]>) => setRoleVisibility(m));

  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [saved, setSaved] = useState(false);

  // Seed local edit state once the stored matrix loads.
  useEffect(() => {
    if (matrixApi.data) setDraft({ ...matrixApi.data.matrix });
  }, [matrixApi.data]);

  const roles = useMemo<RoleRow[]>(
    () => (rolesApi.data?.roles ?? []).filter((r) => r.code !== "admin"),
    [rolesApi.data],
  );

  if (rolesApi.loading || matrixApi.loading) return <Loading />;
  if (rolesApi.error) return <ErrorState error={rolesApi.error} onRetry={rolesApi.reload} />;
  if (matrixApi.error) return <ErrorState error={matrixApi.error} onRetry={matrixApi.reload} />;

  function toggle(wt: WorkTypeId, field: PeopleFieldId, code: string) {
    setSaved(false);
    setDraft((prev) => {
      const key = cellKey(wt, field);
      const cur = new Set(prev[key] ?? []);
      if (cur.has(code)) cur.delete(code);
      else cur.add(code);
      const next = { ...prev };
      const list = Array.from(cur).sort();
      if (list.length) next[key] = list;
      else delete next[key];
      return next;
    });
  }

  async function save() {
    // Only persist applicable, non-empty cells (the backend normalizes too).
    const out: Record<string, string[]> = {};
    for (const wt of WORK_TYPES) {
      for (const f of FIELDS) {
        if (!cellApplies(wt.id, f.id)) continue;
        const key = cellKey(wt.id, f.id);
        const codes = draft[key];
        if (codes && codes.length) out[key] = codes;
      }
    }
    await saveMut.run(out);
    setSaved(true);
    matrixApi.reload();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Picker Visibility</h3>
        <div className="btn-row" style={{ alignItems: "center" }}>
          <button className="btn small primary" onClick={save} disabled={saveMut.pending}>
            {saveMut.pending ? <Spinner /> : "Save"}
          </button>
        </div>
      </div>
      <div className="card-body">
        <p className="sub">
          Choose which roles appear in each people picker. Leave a cell empty to allow all roles
          (the default). Role visibility is a convenience filter, not a security boundary.
        </p>
        {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
        {saved && !saveMut.pending ? <Alert kind="success">Picker visibility saved.</Alert> : null}

        <div className="role-matrix" style={{ display: "grid", gap: 16, marginTop: 12 }}>
          {WORK_TYPES.map((wt) => (
            <fieldset className="role-module" key={wt.id}>
              <legend>{wt.label}</legend>
              <div style={{ display: "grid", gap: 12 }}>
                {FIELDS.map((f) => {
                  const applies = cellApplies(wt.id, f.id);
                  const key = cellKey(wt.id, f.id);
                  const selected = new Set(draft[key] ?? []);
                  return (
                    <div key={f.id}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {f.label}
                        {!applies ? (
                          <span className="hint" style={{ marginLeft: 8, fontWeight: 400 }}>
                            — not a role-based picker for this work type
                          </span>
                        ) : selected.size === 0 ? (
                          <span className="hint" style={{ marginLeft: 8, fontWeight: 400 }}>
                            — all roles
                          </span>
                        ) : null}
                      </div>
                      {applies ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {roles.length === 0 ? (
                            <span className="hint">No roles defined.</span>
                          ) : (
                            roles.map((r) => (
                              <label className="role-perm" key={r.id} title={r.code}>
                                <input
                                  type="checkbox"
                                  checked={selected.has(r.code)}
                                  onChange={() => toggle(wt.id, f.id, r.code)}
                                />
                                {r.name}
                              </label>
                            ))
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>
      </div>
    </div>
  );
}
